import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	Box,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useState } from "react";
import {
	type BillPeriod,
	findOverlappingPeriod,
	formatBillingDate,
} from "../../../shared/utils/billPeriods";
import type { GasBill, GasBillDraft } from "../models/GasBilling";
import { GasBillRow } from "./GasBillRow";

const HEAD_SX = {
	backgroundColor: "#fff",
	borderBottom: "1px solid #e4e4e5",
	borderRight: "1px solid",
	borderColor: "divider",
	color: "text.secondary",
	fontSize: 11,
	fontWeight: 600,
	lineHeight: 1.25,
	px: 0.5,
	py: 0.25,
	textAlign: "center",
	whiteSpace: "normal",
} as const;

const COLUMN_WIDTHS = ["11%", "11%", "13%", "13%", "13%", "13%", "13%", "13%"];

const toDraft = (bill: GasBill): GasBillDraft => ({
	GasBillId: bill.GasBillId,
	ServicePeriodStart: bill.ServicePeriodStart,
	ServicePeriodEnd: bill.ServicePeriodEnd,
	Mcf: bill.Mcf,
	Mmbtu: bill.Mmbtu,
	Cost: bill.Cost,
});

const getDraftError = (
	draft: GasBillDraft,
	periods: BillPeriod[],
): string | null => {
	if (draft.ServicePeriodStart === null || draft.ServicePeriodEnd === null)
		return "The service period needs both dates";
	if (draft.ServicePeriodEnd.getTime() < draft.ServicePeriodStart.getTime())
		return "The end date cannot be before the start date";
	const clash = findOverlappingPeriod(
		periods,
		draft.ServicePeriodStart,
		draft.ServicePeriodEnd,
		draft.GasBillId,
	);
	return clash === null
		? null
		: `Overlaps the bill of ${formatBillingDate(clash.start)} → ${formatBillingDate(clash.end)}`;
};

interface GasBillsTableProps {
	bills: GasBill[];
	periods: BillPeriod[];
	isLoading: boolean;
	onSave: (draft: GasBillDraft) => Promise<unknown>;
	onDelete: (gasBillId: number) => void;
}

export const GasBillsTable = ({
	bills,
	periods,
	isLoading,
	onSave,
	onDelete,
}: GasBillsTableProps) => {
	const [storedDraft, setDraft] = useState<GasBillDraft | null>(null);
	const [pendingBill, setPendingBill] = useState<GasBill | null>(null);
	const editedBill =
		storedDraft === null
			? undefined
			: bills.find((bill) => bill.GasBillId === storedDraft.GasBillId);
	const draft = editedBill === undefined ? null : storedDraft;
	const isDraftDirty =
		draft !== null &&
		editedBill !== undefined &&
		JSON.stringify(draft) !== JSON.stringify(toDraft(editedBill));

	const startEdit = (bill: GasBill) => {
		setDraft(toDraft(bill));
		setPendingBill(null);
	};

	const requestEdit = (bill: GasBill) => {
		if (isDraftDirty) setPendingBill(bill);
		else startEdit(bill);
	};

	if (isLoading && bills.length === 0) {
		return (
			<Paper elevation={2} sx={{ height: "65vh", p: 1 }}>
				<CenteredLazyLoading />
			</Paper>
		);
	}

	const error = draft === null ? null : getDraftError(draft, periods);

	return (
		<Box sx={{ position: "relative" }}>
			{isLoading && bills.length > 0 && (
				<LinearProgress
					sx={{
						borderRadius: "4px 4px 0 0",
						left: 0,
						pointerEvents: "none",
						position: "absolute",
						right: 0,
						top: 0,
						zIndex: 3,
					}}
				/>
			)}
			<TableContainer
				component={Paper}
				elevation={2}
				sx={{
					height: "70vh",
					maxHeight: { xs: 460, lg: "calc(100vh - 260px)" },
				}}
			>
				<Table
					size="small"
					stickyHeader
					sx={{
						"& td": {
							borderRight: "1px solid",
							borderColor: "divider",
							boxSizing: "border-box",
							fontSize: 11,
							height: 30,
							lineHeight: 1.2,
							px: 0.5,
							py: 0,
						},
						tableLayout: "fixed",
					}}
				>
					<colgroup>
						{COLUMN_WIDTHS.map((width, index) => (
							<col key={index} style={{ width }} />
						))}
					</colgroup>
					<TableHead>
						<TableRow>
							<TableCell sx={HEAD_SX}>Start Date</TableCell>
							<TableCell sx={HEAD_SX}>End Date</TableCell>
							<TableCell sx={HEAD_SX}>MCF</TableCell>
							<TableCell sx={HEAD_SX}>MMBtu</TableCell>
							<TableCell sx={HEAD_SX}>Cost ($)</TableCell>
							<TableCell sx={HEAD_SX}>Heat value (BTU/cf)</TableCell>
							<TableCell sx={HEAD_SX}>Cost per MCF ($)</TableCell>
							<TableCell sx={HEAD_SX} />
						</TableRow>
					</TableHead>
					<TableBody>
						{bills.length === 0 && (
							<TableRow>
								<TableCell colSpan={COLUMN_WIDTHS.length} sx={{ border: 0 }}>
									<Typography
										sx={{
											color: "text.secondary",
											fontSize: "0.85rem",
											py: 6,
											textAlign: "center",
										}}
									>
										No gas bills entered for this period yet.
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{bills.map((bill) => {
							const isEditing = draft?.GasBillId === bill.GasBillId;
							return (
								<GasBillRow
									key={bill.GasBillId}
									bill={bill}
									draft={isEditing ? draft : null}
									periods={periods}
									error={isEditing ? error : null}
									isLocked={draft !== null && !isEditing}
									isSaving={isLoading}
									onPatch={(changes: Partial<GasBillDraft>) =>
										setDraft((current) =>
											current === null ? null : { ...current, ...changes },
										)
									}
									onEdit={() => requestEdit(bill)}
									onCancel={() => setDraft(null)}
									onSave={() => {
										if (draft === null) return;
										onSave(draft).then(
											() => setDraft(null),
											() => {},
										);
									}}
									onDelete={() => onDelete(bill.GasBillId)}
								/>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
			<ConfirmationDialogRaw
				title="Discard changes"
				message="This bill has unsaved changes. Discard them?"
				open={pendingBill !== null}
				buttons={{ no: "Keep editing", yes: "Discard" }}
				onClose={(value) => {
					if (value === "OK" && pendingBill !== null) startEdit(pendingBill);
					else setPendingBill(null);
				}}
			/>
		</Box>
	);
};
